import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from '@reach/router'
import { useTranslation } from '@/utils/useTranslation'
import tw from 'twin.macro'

import Link from '@/components/link-component'
import Disclosure from '@/components/ui/disclosure'
import { useSessionStorage } from 'usehooks-ts'
import { getLocale, localizedPath } from '@plugins/bw-i18next'
// import BwiPlus from '../icons/bwi/plus-square.svg'
// import BwiMinus from '../icons/bwi/minus-square.svg'

export default function HelpNav({ helpNavNodes, helpNavInitial }): JSX.Element {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  // help remember state between page loads
  const helpNav = useRef(null)
  const [indexes, setIndexes] = useSessionStorage('helpNavIndexes', [])
  const [scrollY, setScrollY] = useSessionStorage<number>('helpNavScrollY', 0)

  const HelpLink = useCallback(({ children, slug = null }) => {
    const path = localizedPath(!slug ? '/help/' : `/help/${slug}/`, getLocale(pathname))
    return (
      <Link
        to={path}
        tw="text-indigoBlue px-4 py-2 rounded-full leading-tight w-full block hover:(bg-gray-200)"
        css={[pathname === path && tw`!bg-indigoBlue border-indigoBlue text-white`]}
        onClick={() => setScrollY(helpNav?.current?.scrollTop)}
      >
        {children}
      </Link>
    )
  }, [])

  const disclosureClickHandler = useCallback((d0, d1 = undefined, d2 = undefined) => {
    setIndexes((prev) => {
      if (d1 === undefined) {
        if (!prev[d0]) prev[d0] = { expanded: true, children: [] }
        else prev[d0].expanded = !prev[d0].expanded
      } else if (d2 === undefined) {
        if (!prev[d0].children[d1]) prev[d0].children[d1] = { expanded: true, children: [] }
        else prev[d0].children[d1].expanded = !prev[d0].children[d1].expanded
      } else {
        // eslint-disable-next-line no-lonely-if
        if (!prev[d0].children[d1].children[d2]) prev[d0].children[d1].children[d2] = { expanded: true }
        else prev[d0].children[d1].children[d2].expanded = !prev[d0].children[d1].children[d2].expanded
      }
      return prev
    })
  }, [])

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      sessionStorage.removeItem('helpNavIndexes')
      sessionStorage.removeItem('helpNavScrollY')
    })
    if (helpNav?.current?.scrollTo && scrollY > 0) (helpNav.current as HTMLElement).scrollTo(0, scrollY)
  }, [])

  return (
    <nav
      tw="text-indigoBlue h-fit overflow-y-auto pt-2 pb-12 max-h-full lg:(max-h-[calc(100vh-10rem)] my-4)"
      ref={helpNav}
    >
      <div tw="px-2">
        {/* <div tw="absolute right-4 top-0 z-10 p-1 text-gray-400">
        <span
          tw="font-bwi mr-1 cursor-pointer w-16 h-16 hover:(text-gray-700)"
          title="Expand all categories"
          onClick={() => {
            setIndexes(Array.from(Array(nodes.items.length), (item, index) => index))
          }}
        >&#xe921;</span>
        <span
          tw="font-bwi mr-1 cursor-pointer hover:(text-gray-700)"
          title="Colapse all categories"
          onClick={() => setIndexes([])}
        >&#xe925;</span>
      </div> */}

        <HelpLink>{t('Home')}</HelpLink>
        <HelpLink slug="releasenotes">{t('Release Notes')}</HelpLink>
        {helpNavNodes.map((node, d0) => (
          <ul key={node.title} tw="list-none mt-2">
            <li>
              <Disclosure
                heading={<span tw="font-bold text-lg">{node.title}</span>}
                defaultOpen={helpNavInitial?.[0]?.index === d0 || indexes[d0]?.expanded === true}
                onClick={() => disclosureClickHandler(d0)}
              >
                {node.items.map((item, d1) => (
                  <ul key={item.title} tw="list-none ml-2">
                    {item.slug && (
                      <li>
                        <HelpLink key={item.slug} slug={item.slug}>
                          {item.title}
                        </HelpLink>
                      </li>
                    )}
                    {item.items && (
                      <li>
                        <Disclosure
                          heading={<span tw="font-bold">{item.title}</span>}
                          defaultOpen={
                            (helpNavInitial?.[0]?.index === d0 && helpNavInitial?.[1]?.index === d1) ||
                            indexes[d0]?.children[d1]?.expanded === true
                          }
                          onClick={() => disclosureClickHandler(d0, d1)}
                        >
                          {item.items.map((subItem, d2) => (
                            <ul key={subItem.title} tw="list-none ml-2">
                              {subItem.slug && (
                                <li>
                                  <HelpLink key={subItem.slug} slug={subItem.slug}>
                                    {subItem.title}
                                  </HelpLink>
                                </li>
                              )}
                              {subItem.items && (
                                <li>
                                  <Disclosure
                                    heading={<span tw="font-bold">{subItem.title}</span>}
                                    defaultOpen={
                                      (helpNavInitial?.[0]?.index === d0 &&
                                        helpNavInitial?.[1]?.index === d1 &&
                                        helpNavInitial?.[2]?.index === d2) ||
                                      indexes[d0]?.children[d1]?.children[d2]?.expanded === true
                                    }
                                    onClick={() => disclosureClickHandler(d0, d1, d2)}
                                  >
                                    {subItem.items.map((subSubItem) => (
                                      <ul key={subSubItem.title} tw="list-none ml-2">
                                        {subSubItem.slug && (
                                          <li>
                                            <HelpLink key={subSubItem.slug} slug={subSubItem.slug}>
                                              {subSubItem.title}
                                            </HelpLink>
                                          </li>
                                        )}
                                      </ul>
                                    ))}
                                  </Disclosure>
                                </li>
                              )}
                            </ul>
                          ))}
                        </Disclosure>
                      </li>
                    )}
                  </ul>
                ))}
              </Disclosure>
            </li>
          </ul>
        ))}
      </div>
    </nav>
  )
}
